/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { produce } from "immer";
import type { HandleChangeValues } from "@layout/app/LayoutContext";
import { createLocalZustand } from "@/shared/state";
import {
  reportError,
  __resetErrorNotificationDebounce,
} from "@/shared/ui/components/basic/error-notification/report-error";

export type globalErrorType = Record<string, any> | null;

type DevSimulatorModule = typeof import('@features/dev-cockpit/services/state-simulator');

/**
 * Test-only compatibility hook. The dev-cockpit "Error" simulation now fails at
 * the HTTP layer (see `request.ts`), so the error engine no longer needs the
 * simulator module; this setter is retained as a no-op for existing tests.
 *
 * @internal
 * @param {DevSimulatorModule | null} _mod - Ignored simulator module.
 * @returns {void}
 */
export function __setDevSimulatorForTest(_mod: DevSimulatorModule | null): void {
  // no-op: surfacing is delegated to reportError; the HTTP layer drives the sim.
}

/**
 * Resets the shared error-notification debounce window. Delegates to the single
 * engine in `report-error.ts`. Test-only hook retained for compatibility.
 *
 * @returns {void}
 */
export function __resetNetworkErrorCoalesce(): void {
  __resetErrorNotificationDebounce();
}

interface globalErrorContextType {
  globalError: globalErrorType;
  set: HandleChangeStore;
  get: () => globalErrorContextType;
  setGlobalError: (
    error: globalErrorType,
    navigate?: (path: string, options?: { replace?: boolean }) => void,
    setPopup?: HandleChangeValues
  ) => void;
  clearGlobalError: () => void;
}

export type HandleChangeStore = (
  partial:
    | globalErrorContextType
    | Partial<globalErrorContextType>
    | ((
        state: globalErrorContextType
      ) => globalErrorContextType | Partial<globalErrorContextType>),
  replace?: boolean | undefined
) => void;

/**
 * Reads the routing-relevant status code from an error, preferring `code`,
 * then `status`, then `message` (matching legacy resolution order).
 *
 * @param {globalErrorType} error - The error to inspect.
 * @returns {*} The resolved status code, or `undefined`.
 */
function getStatusCode(error: globalErrorType): any {
  return error?.code || error?.status || error?.message;
}

/**
 * True when a 403 or 404 error should redirect to the workspace rather than
 * surface a toast. This prevents leaking private layout/content for agency
 * resources the user has no access to (or that do not exist).
 *
 * @param {*} statusCode - The resolved status code from the error.
 * @param {Function} [navigate] - Router navigate function, when available.
 * @returns {boolean} True when the error was handled by navigation.
 */
function routeForbiddenToApp(
  statusCode: any,
  navigate?: (path: string, options?: { replace?: boolean }) => void
): boolean {
  if ((statusCode === 403 || statusCode === 404) && navigate) {
    navigate("/app/workspace", { replace: true });
    return true;
  }
  return false;
}

/**
 * Serializes an error for Zustand state storage. Native Error objects are not
 * JSON-serializable, so enumerable and relevant non-enumerable properties are
 * extracted explicitly.
 *
 * @param {globalErrorType} error - The error to serialize.
 * @returns {globalErrorType} A plain, storable representation of the error.
 */
function serializeError(error: globalErrorType): globalErrorType {
  if (error === null || error === undefined) {
    return error;
  }
  if (error instanceof Error) {
    return serializeNativeError(error);
  }
  try {
    return JSON.parse(JSON.stringify(error));
  } catch {
    return { message: String(error) };
  }
}

/**
 * Extracts name, message, stack, and own properties from a native Error.
 *
 * @param {Error} error - The native error to flatten.
 * @returns {Record<string, any>} The flattened, storable error.
 */
function serializeNativeError(error: Error): Record<string, any> {
  const serialized: Record<string, any> = {
    name: error.name,
    message: error.message,
  };
  if (error.stack) {
    serialized.stack = error.stack;
  }
  for (const key of Object.keys(error)) {
    serialized[key] = (error as any)[key];
  }
  return serialized;
}

export const globalErrorContext = createLocalZustand<globalErrorContextType>(
  (initializer) => (set, get) => ({
    globalError: null,
    set: set,
    get: get,
/**
 * Stores the error, logs it, and surfaces it. A 403 with a navigate function
 * routes to the full-page app shell; every other error is surfaced through the
 * single unified notifier. `setPopup` is retained for signature compatibility
 * but no longer renders a modal — the toast is the single imperative surface.
 *
 * @param {globalErrorType} error - The error to handle.
 * @param {Function} [navigate] - Router navigate function for routing decisions.
 * @param {HandleChangeValues} [_setPopup] - Deprecated, unused popup setter.
 * @returns {void}
 */
    setGlobalError: (error, navigate, _setPopup) => {
      set(
        produce((state) => {
          state.globalError = serializeError(error);
        })
      );

      console.error(error);
      const statusCode = getStatusCode(error);
      if (routeForbiddenToApp(statusCode, navigate)) {
        return;
      }
      reportError(error);
    },
/**
 * Clears the stored global error.
 *
 * @returns {void}
 */
    clearGlobalError: () => {
      set(
        produce((state) => {
          state.globalError = null;
        })
      );
    },
  })
);

export const { useStore: useGlobalError } = globalErrorContext;
